import React from "react";
import { Box, MenuItem, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import logo from "../../img/logo.png";

const drawerWidth = 200;
const headerHeight = 64;

// Same sticky/RTL-right technique as the only existing persistent-sidebar
// precedent in the repo, src/components/AdminNavbar.js's default export —
// mirrored here, not imported, since that component is Admin-styled and
// out of scope for the student redesign.
const navItemSx = (isRtl) => ({
  display: "flex",
  flexDirection: isRtl ? "row-reverse" : "row",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  gap: 1.25,
  borderRadius: "12px",
  px: 1.5,
  py: 1.3,
  color: "#152355",
  "& .MuiSvgIcon-root": { color: "#6986b6", fontSize: 22, flexShrink: 0 },
  "&.Mui-selected": {
    bgcolor: "rgba(47,111,237,0.10)",
    color: "#2f6fed",
    "& .MuiSvgIcon-root": { color: "#2f6fed" },
  },
  "&:hover": { bgcolor: "rgba(47,111,237,0.06)" },
});

const navItemTextSx = (isRtl) => ({
  fontSize: 14.5,
  fontWeight: 700,
  flex: 1,
  minWidth: 0,
  textAlign: isRtl ? "right" : "left",
});

export default function StudentSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language || "ar") === "rtl";

  const items = [
    { key: "home", label: t("Home page"), icon: <GridViewOutlinedIcon />, path: "/student/classes" },
    { key: "messages", label: t("Messages"), icon: <ChatBubbleOutlineOutlinedIcon />, path: "/student/chat" },
    { key: "support", label: t("Support"), icon: <SupportAgentOutlinedIcon />, path: "/student/contactUs" },
  ];

  return (
    <Box
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        bgcolor: "#ffffff",
        borderLeft: isRtl ? "1px solid #eef1f6" : "none",
        borderRight: isRtl ? "none" : "1px solid #eef1f6",
        height: "100vh",
        position: "sticky",
        top: 0,
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
    >
      <Box
        sx={{
          height: headerHeight,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          px: 2,
        }}
      >
        <Box component="img" src={logo} alt={t("Logo")} sx={{ width: 118, height: "auto" }} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1.5, pt: 3 }}>
        {items.map((item) => (
          <MenuItem
            key={item.key}
            selected={location.pathname === item.path}
            onClick={() => navigate(item.path)}
            sx={navItemSx(isRtl)}
          >
            {item.icon}
            <Typography sx={navItemTextSx(isRtl)}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Box>
    </Box>
  );
}
